export const VideoCategoryStatus = {
  Offline: 0,
  Ready: 1,
  Online: 2,
} as const;

export type VideoCategoryStatus =
  (typeof VideoCategoryStatus)[keyof typeof VideoCategoryStatus];

/**
 * Returns the Typesense filter_by clause for category status.
 * - Regular users: only Online (2)
 * - Admins: Online (2) + Ready (1)
 * - Offline (0) is always hidden
 */
export const getCategoryStatusFilter = (isAdmin: boolean): string =>
  isAdmin ? 'status:=[1,2]' : 'status:=2';

export interface VideoCategory {
  asset_count?: number;
  breadcrumb_name?: string;
  custom_query?: string;
  description?: string;
  embedding?: number[];
  embedding_full?: number[];
  featured_groups?: MvpZoneGroupedCategory[];
  id: string;
  is_zone: boolean;
  keywords?: string[];
  meta_description?: string;
  meta_title?: string;
  minimum_video_count?: number;
  name: string;
  name_sanitized?: string;
  primary_heading?: string;
  primary_heading_visitor?: string;
  slug: string;
  status?: VideoCategoryStatus;
  thumbnail_url?: string;
  use_exact_keyword_match?: boolean;
  use_full_embedding?: boolean;
  vector_distance_threshold?: number;
}

// Embeddings are only returned when named (see client.ts), and Typesense
// can't add them to `*`, so category reads list every field they use.
export const VIDEO_CATEGORY_FIELDS = [
  'asset_count',
  'breadcrumb_name',
  'custom_query',
  'description',
  'embedding',
  'embedding_full',
  'id',
  'is_zone',
  'keywords',
  'meta_description',
  'meta_title',
  'minimum_video_count',
  'name',
  'name_sanitized',
  'primary_heading',
  'primary_heading_visitor',
  'slug',
  'status',
  'thumbnail_url',
  'use_exact_keyword_match',
  'use_full_embedding',
  'vector_distance_threshold',
].join(',');

export interface VideoCategoryGroup {
  custom_query?: string;
  embedding?: number[];
  embedding_full?: number[];
  id: string;
  name: string;
  name_sanitized?: string;
  slug: string;
}

export const VIDEO_CATEGORY_GROUP_FIELDS = [
  'custom_query',
  'embedding',
  'embedding_full',
  'id',
  'name',
  'name_sanitized',
  'slug',
].join(',');

export interface MvpZoneGroupedCategory {
  categories: VideoCategory[];
  group: VideoCategoryGroup;
}

export interface VideoDocument {
  aspect_ratio: string;
  batch_id: number;
  contributor_id: number;
  contributor_image?: string;
  contributor_name: string;
  contributor_slug: string;
  description: string;
  duration_seconds: number;
  embedding: number[];
  file_size_bytes_4k: number;
  file_size_bytes_720p: number;
  file_size_bytes_1080p: number;
  frame_rate: number;
  has_4k: boolean;
  has_720p: boolean;
  has_1080p: boolean;
  id: string; // string, but parseable as number
  is_exclusive: boolean;
  is_premium: boolean;
  keywords: string[];
  orientation: StockVideoOrientation;
  preview_url: string;
  set_live?: number;
  slug: string;
  thumbnail_url: string;
  title: string;
  updated_at: number;
  vector_distance?: number; // Debug field from Typesense vector search
  // Left-joined engagement row for the searched (slug, account_type_group).
  // Present only when the search uses STOCK_VIDEO_INCLUDE_FIELDS.
  videoEngagementV1?: {
    account_type_group?: 'free' | 'paid';
    decay_downloads?: number;
    decay_plays?: number;
    slug_mean?: number;
    sort_score?: number;
    total_downloads?: number;
    total_plays?: number;
  };
}

export type StockVideoOrientation =
  | 'Horizontal'
  | 'Vertical'
  | 'Square'
  | 'Ultra-wide';

export type StockVideoOrientationLowerCase =
  | 'horizontal'
  | 'vertical'
  | 'square'
  | 'ultra-wide';

export type GridRowLimits = Partial<Record<'md' | 'lg' | 'xl', number>>;

export interface BreakpointFlags {
  isLg?: boolean;
  isMd?: boolean;
  isXl?: boolean;
}
